import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ADMIN_NAV,
  centsToPesos,
  DashboardFinancials,
  flattenNav,
  TRANSACTION_STATUSES,
  TRANSACTION_STATUS_LABELS,
  type TransactionStatus,
} from '@cannery/shared';
import { useAuth } from '../../auth/AuthContext';
import { api } from '../../lib/api';
import { Badge, Card, ErrorBanner, Spinner } from '../../components/ui';
import { Icon, type IconName } from '../../components/Icon';
import { SEVERITY_TONE, formatAge } from './ExceptionsPage';

const MODULE_ICONS: Record<string, IconName> = {
  dashboard: 'dashboard',
  transactions: 'transactions',
  monitoring: 'monitoring',
  washing: 'washing',
  folding: 'folding',
  pickup: 'pickup',
  delivery: 'delivery',
  purchasing: 'purchasing',
  inventory: 'inventory',
  expenses: 'expenses',
  reports: 'reports',
  settings: 'settings',
  users: 'users',
  audit: 'shield',
  backup: 'database',
  exceptions: 'alert',
  accountability: 'users',
  comparison: 'reports',
  discrepancies: 'alert',
  payments: 'expenses',
  reconciliation: 'shield',
  handover: 'delivery',
  'control-review': 'shield',
};

const MODULE_HINTS: Record<string, string> = {
  transactions: 'Receive and record new laundry',
  monitoring: 'Follow every transaction, oldest first',
  washing: 'Wash and dry working area',
  folding: 'Folding working area',
  pickup: 'Orders ready for customer pickup',
  delivery: 'Orders ready for delivery',
  purchasing: 'Supplies & purchases',
  inventory: 'Stock levels per branch',
  expenses: 'Operating expenses per branch',
  reports: 'Business reports',
  settings: 'Branches, pricing & configuration',
  users: 'Accounts, roles & branch access',
  audit: 'Who did what, when, and where',
  backup: 'Server-side backups & recovery',
  exceptions: 'Aging, discrepancies & stuck work',
  accountability: 'Who did what, when and where',
  comparison: 'Every branch side by side, facts only',
  discrepancies: 'Item-count differences by stage',
  payments: 'Collections, balances & payment status',
  reconciliation: 'Counted vs recorded, per branch and day',
  handover: 'Unfinished work, who hands it over',
  'control-review': 'Investigate exceptions & potential losses',
};

/** The 5 statuses to display in the panel (excludes RELEASED). */
const PANEL_STATUSES: TransactionStatus[] = ['PENDING', 'WASHING', 'FOLDING', 'READY', 'DONE'];

/** Colors for the 5 status donut segments (sky blue family). */
const STATUS_DONUT_COLORS = ['#94a3b8', '#2498e8', '#f59e0b', '#3b82f6', '#60a5fa'];

/** Human labels for the 5 statuses displayed in the panel. */
const PANEL_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pending',
  WASHING: 'Washing',
  FOLDING: 'Folding',
  READY: 'Ready',
  DONE: 'Completed',
};

/** Active queue statuses shown as separate panels beneath the main grid. */
const QUEUE_STATUSES = ['PENDING', 'WASHING', 'FOLDING', 'READY'] as const;
type QueueStatus = (typeof QUEUE_STATUSES)[number];

/** Newest rows loaded per queue panel; the badge shows the full server total. */
const QUEUE_PANEL_LIMIT = 25;

/**
 * Shared hover treatment for every dashboard card/panel — identical to the
 * Quick access reference card: border transitions to the sky blue accent with
 * a restrained ring shadow. Only border-color and box-shadow animate, so
 * hover never shifts layout; index.css collapses the transition to an
 * instant state change under prefers-reduced-motion. Cards that already
 * lift on hover (KPI, Quick access) keep their own translate utility.
 */
const PANEL_HOVER =
  'transition-all hover:border-accent/40 hover:shadow-[0_0_0_1px_rgba(36,152,232,0.25),0_22px_44px_-26px_rgba(36,152,232,0.35)]';

/* ============================================================
 * API response shapes (verified against the API modules)
 * ============================================================ */

/** Phase 2G.2 - aggregate from GET /api/exceptions (admin-only, read-only). */
interface AttentionResponse {
  total: number;
  generatedAt: string;
  severityCounts: Record<string, number>;
  items: {
    id: string;
    severity: 'ERROR' | 'ATTENTION' | 'INFO';
    categoryLabel: string;
    ageMinutes: number;
    transaction: { number: string };
  }[];
}

/** Phase 2G.3 - aggregate from GET /api/accountability/summary?date=TODAY. */
interface AccountabilityTodayResponse {
  total: number;
  timeZone: string;
  items: { employee: { id: string }; counts: Record<string, number> }[];
}

/** GET /api/payments list item (PaymentRowDto in the payments module). */
interface PaymentItem {
  id: string;
  number: string;
  branch: { id: string; code: string };
  customerName: string;
  workflowStatusLabel: string;
  totalCents: number;
  amountPaidCents: number;
  paymentStatus: string;
  paymentStatusLabel: string;
  paymentMethod: string | null;
  paymentRecordedAt: string;
}

interface PaymentsListResponse {
  total: number;
  items: PaymentItem[];
}

/** GET /api/payments/collections - server-side, single-count basis. */
interface CollectionsTotals {
  transactions: number;
  payments: number;
  totalCents: number;
  collectedCents: number;
  outstandingCents: number;
  overpaidCents: number;
}

interface CollectionsResponse {
  timeZone: string;
  generatedAt: string;
  totals: CollectionsTotals;
  byMethod: { method: string | null; payments: number; collectedCents: number }[];
}

/** One row from GET /api/transactions (fields used by the queue panels). */
interface TransactionListItem {
  id: string;
  number: string;
  branch: { id: string; code: string };
  status: string;
  customerName: string;
  totalCents: number;
  paymentMethod: string | null;
  readyAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** GET /api/transactions list envelope - statusCounts covers every bucket. */
interface TransactionsListResponse {
  total: number;
  statusCounts: Record<string, number>;
  items: TransactionListItem[];
}

/** Per-status list payloads for the four queue panels. */
type StatusQueues = Record<QueueStatus, TransactionsListResponse>;

/**
 * Recency timestamp for a queue panel. Pending uses creation time (how long
 * it has been waiting), Ready uses the recorded READY transition when present,
 * and Washing/Folding use the transaction's last activity (`updatedAt`) —
 * per-stage history timestamps are only exposed by the detail endpoint.
 */
function queueActivityAt(tx: TransactionListItem, status: QueueStatus): number {
  const iso =
    status === 'PENDING'
      ? tx.createdAt
      : status === 'READY'
        ? (tx.readyAt ?? tx.updatedAt)
        : tx.updatedAt;
  return new Date(iso).getTime();
}

/** GET /api/inventory list envelope. */
interface InventoryListResponse {
  total: number;
  lowStockCount: number;
}

interface ExpenseListResponse {
  total: number;
  items: { id: string; amountCents: number }[];
}

interface PurchaseListResponse {
  total: number;
  items: { id: string; totalCents: number }[];
}

/* ============================================================
 * Manila business-date helpers (browser-local time is never
 * authoritative - the API interprets these strings as Asia/Manila).
 * ============================================================ */

const MANILA_TZ = 'Asia/Manila';

function manilaToday(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: MANILA_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/** Calendar arithmetic on a yyyy-mm-dd string (UTC-safe for date-only). */
function shiftDate(dateStr: string, days: number): string {
  const d = new Date(`${dateStr}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Manila day key (MM-DD) for grouping a server timestamp. */
function manilaDayKey(iso: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: MANILA_TZ,
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso));
}

/** "Oct 1" style label from a yyyy-mm-dd string (Manila-scoped input). */
function dayLabel(dateStr: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
  }).format(new Date(`${dateStr}T00:00:00.000Z`));
}

function clockTime(d: Date): string {
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/* ============================================================
 * Trend chart - hand-rolled SVG (no chart dependency exists or
 * was added). Single series: transactions received per Manila day.
 * ============================================================ */

interface TrendPoint {
  date: string; // yyyy-mm-dd (Manila)
  count: number;
  collectedCents: number;
}

function TrendChart({ points, emptyText }: { points: { date: string; sales: number; expenses: number }[]; emptyText: string }) {
  const [hover, setHover] = useState<number | null>(null);
  const W = 720;
  const H = 240;
  const maxSales = Math.max(1, ...points.map((p) => p.sales));
  const maxExpenses = Math.max(1, ...points.map((p) => p.expenses));
  const yMax = Math.max(4, Math.ceil(Math.max(maxSales, maxExpenses) / 4) * 4);
  const step = points.length > 1 ? W / (points.length - 1) : W;
  const x = (i: number) => (points.length > 1 ? i * step : W / 2);
  const y = (v: number) => H - (v / yMax) * (H - 16);
  const salesLine = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.sales).toFixed(1)}`).join(' ');
  const expensesLine = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(p.expenses).toFixed(1)}`).join(' ');
  const salesArea = `${salesLine} L${x(points.length - 1).toFixed(1)},${H} L${x(0).toFixed(1)},${H} Z`;
  const expensesArea = `${expensesLine} L${x(points.length - 1).toFixed(1)},${H} L${x(0).toFixed(1)},${H} Z`;
  const hasData = points.some((p) => p.sales > 0 || p.expenses > 0);
  const active = hover !== null ? points[hover] : null;

  return (
    <div className="relative">
      {!hasData && (
        <p className="absolute inset-x-0 top-16 z-10 text-center text-sm text-slate-500">{emptyText}</p>
      )}
      <div className="flex gap-3">
        <div className="flex h-64 w-8 flex-col justify-between pb-5 pt-1 text-right text-xs text-slate-600">
          {[0, yMax / 4, yMax / 2, (yMax * 3) / 4, yMax]
            .slice()
            .reverse()
            .map((t) => (
              <span key={t} className="tabular-nums">
                {t}
              </span>
            ))}
        </div>
        <div
          className="relative h-64 min-w-0 flex-1 cursor-crosshair pb-5"
          onMouseMove={(e) => {
            const r = e.currentTarget.getBoundingClientRect();
            const px = e.clientX - r.left;
            const idx = Math.max(0, Math.min(points.length - 1, Math.round(px / (r.width / Math.max(1, points.length - 1)))));
            setHover(idx);
          }}
          onMouseLeave={() => setHover(null)}
        >
          <svg
            viewBox={`0 0 ${W} ${H}`}
            preserveAspectRatio="none"
            className="absolute inset-x-0 top-0 h-[calc(100%-1.25rem)] w-full overflow-visible"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="chartSalesGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#2498e8" stopOpacity="0.25" />
                <stop offset="1" stopColor="#2498e8" stopOpacity="0" />
              </linearGradient>
              <linearGradient id="chartExpensesGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#f59e0b" stopOpacity="0.28" />
                <stop offset="1" stopColor="#f59e0b" stopOpacity="0" />
              </linearGradient>
            </defs>
            {/* Sales area (sky blue) */}
            <path d={salesArea} fill="url(#chartSalesGradient)" />
            {/* Expenses area (amber) */}
            <path d={expensesArea} fill="url(#chartExpensesGradient)" />
            {/* Sales line (sky blue) */}
            <path
              d={salesLine}
              fill="none"
              stroke="#2498e8"
              strokeWidth="2.5"
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
            {/* Expenses line (amber) */}
            <path
              d={expensesLine}
              fill="none"
              stroke="#f59e0b"
              strokeWidth="2.5"
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
          {/* Crosshair + dot (HTML, not SVG: the viewBox is stretched) */}
          {active && hover !== null && (
            <div
              className="pointer-events-none absolute top-0 border-l border-dashed border-accent/80"
              style={{
                left: `${(hover / Math.max(1, points.length - 1)) * 100}%`,
                height: 'calc(100% - 1.25rem)',
              }}
            />
          )}
          {/* X labels under the plot */}
          <div className="absolute inset-x-0 bottom-0 h-5 text-[11px] text-slate-600">
            {points.map((p, i) => (
              <span
                key={p.date}
                className={`absolute whitespace-nowrap tabular-nums ${
                  i === 0 ? '' : '-translate-x-1/2'
                }`}
                style={{ left: `${(i / Math.max(1, points.length - 1)) * 100}%` }}
              >
                {new Date(`${p.date}T00:00:00.000Z`).getUTCDate()}
              </span>
            ))}
          </div>
          {/* Hover tooltip */}
          {active && (
            <div
              className="pointer-events-none absolute top-2 z-10 min-w-36 -translate-x-1/2 animate-pop rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs shadow-lg"
              style={{
                left: `${(hover! / Math.max(1, points.length - 1)) * 100}%`,
              }}
            >
              <p className="font-semibold text-slate-900">{dayLabel(active.date)}</p>
              <p className="mt-0.5 text-accent-deep tabular-nums">{centsToPesos(active.sales)} sales</p>
              <p className="tabular-nums text-slate-500">{centsToPesos(active.expenses)} expenses</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================================================
 * Section loading skeleton (restrained shimmer)
 * ============================================================ */

function Skeleton({ className = '' }: { className?: string }) {
  return <div className={`animate-pulse rounded-xl bg-slate-200/70 ${className}`} aria-hidden="true" />;
}

/* ============================================================
 * Transaction Status donut - shows counts for 5 statuses.
 * Uses same donut visualization as Collections, but displays
 * transaction status data instead of payment method data.
 * Legend is placed below the centered donut for better visibility.
 * ============================================================ */

function StatusDonut({ statusCounts }: { statusCounts: Record<string, number> }) {
  const segments = PANEL_STATUSES.map((status) => ({
    status,
    count: statusCounts[status] ?? 0,
  })).filter((s) => s.count > 0);
  const totalCount = segments.reduce((sum, s) => sum + s.count, 0);
  const R = 44;
  const C = 2 * Math.PI * R;
  let offset = 0;

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="relative h-36 w-36 shrink-0">
        <svg viewBox="0 0 140 140" className="h-full w-full -rotate-90">
          <circle cx="70" cy="70" r={R} fill="none" stroke="#e2e8f0" strokeWidth="14" />
          {totalCount > 0 &&
            segments.map((seg, i) => {
              const frac = seg.count / totalCount;
              const len = Math.max(0, frac * C - 3); // gap between arcs
              const dash = `${len} ${C - len}`;
              const dashOffset = -offset;
              offset += frac * C;
              return (
                <circle
                  key={seg.status}
                  cx="70"
                  cy="70"
                  r={R}
                  fill="none"
                  stroke={STATUS_DONUT_COLORS[i % STATUS_DONUT_COLORS.length]}
                  strokeWidth="14"
                  strokeDasharray={dash}
                  strokeDashoffset={dashOffset}
                  strokeLinecap="round"
                />
              );
            })}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-base font-bold tabular-nums text-slate-900">{totalCount}</span>
          <span className="text-[10px] text-slate-500">Total</span>
        </div>
      </div>
      <ul className="w-full space-y-1 text-xs">
        {segments.length === 0 && <li className="text-center text-xs text-slate-500">No transactions in current scope.</li>}
        {PANEL_STATUSES.map((status, i) => {
          const count = statusCounts[status] ?? 0;
          if (count === 0) return null;
          const color = STATUS_DONUT_COLORS[i];
          return (
            <li key={status} className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-2 text-slate-600">
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ background: color }}
                  aria-hidden="true"
                />
                <span className="leading-tight">{PANEL_STATUS_LABELS[status]}</span>
              </span>
              <span className="font-semibold tabular-nums text-slate-900">{count}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function StatusDonutCompact({ statusCounts }: { statusCounts: Record<string, number> }) {
  const segments = PANEL_STATUSES.map((status) => ({
    status,
    count: statusCounts[status] ?? 0,
  })).filter((s) => s.count > 0);
  const totalCount = segments.reduce((sum, s) => sum + s.count, 0);
  // The arc radius nearly fills the viewBox so the ring uses the whole box
  // (the old 140-box/R-40 ring rendered at ~57% of its container).
  const R = 42;
  const C = 2 * Math.PI * R;
  let offset = 0;

  return (
    <div className="relative h-full w-full">
      <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90" aria-hidden="true">
        <circle cx="50" cy="50" r={R} fill="none" stroke="#e2e8f0" strokeWidth="12" />
        {totalCount > 0 &&
          segments.map((seg, i) => {
            const frac = seg.count / totalCount;
            const len = Math.max(0, frac * C - 2.5); // gap between arcs
            const dash = `${len} ${C - len}`;
            const dashOffset = -offset;
            offset += frac * C;
            return (
              <circle
                key={seg.status}
                cx="50"
                cy="50"
                r={R}
                fill="none"
                stroke={STATUS_DONUT_COLORS[i % STATUS_DONUT_COLORS.length]}
                strokeWidth="12"
                strokeDasharray={dash}
                strokeDashoffset={dashOffset}
                strokeLinecap="round"
              />
            );
          })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-xl font-bold tabular-nums text-slate-900">{totalCount}</span>
        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-600">Total</span>
      </div>
    </div>
  );
}

/* ============================================================
 * Dashboard
 * ============================================================ */

type SectionKey =
  | 'today'
  | 'collectionsToday'
  | 'queues'
  | 'statusQueues'
  | 'inventory'
  | 'expenses'
  | 'purchases'
  | 'attention'
  | 'account'
  | 'dashboardFinancials'

interface DashboardData {
  today: PaymentsListResponse | null;
  collectionsToday: CollectionsResponse | null;
  queues: TransactionsListResponse | null;
  statusQueues: StatusQueues | null;
  inventory: InventoryListResponse | null;
  expenses: ExpenseListResponse | null;
  purchases: PurchaseListResponse | null;
  attention: AttentionResponse | null;
  account: AccountabilityTodayResponse | null;
  dashboardFinancials: DashboardFinancials | null;
}

const EMPTY_DATA: DashboardData = {
  today: null,
  collectionsToday: null,
  queues: null,
  statusQueues: null,
  inventory: null,
  expenses: null,
  purchases: null,
  attention: null,
  account: null,
  dashboardFinancials: null,
};

type FinancialPeriod = 'daily' | 'weekly' | 'monthly' | 'yearly' | 'custom';

export function AdminDashboardPage() {
  const { user, branchFilter, branches } = useAuth();
  const scopeLabel =
    branchFilter === 'ALL'
      ? 'All branches'
      : (branches.find((b) => b.code === branchFilter)?.name ?? branchFilter);

  const [data, setData] = useState<DashboardData>(EMPTY_DATA);
  const [failed, setFailed] = useState<SectionKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [period, setPeriod] = useState<FinancialPeriod>('monthly');
  // Monotonic generation: a branch switch (or manual refresh) superseding an
  // in-flight load discards the stale response instead of committing it.
  const generation = useRef(0);
  // Branch scope of the last committed load — lets a same-scope refresh keep
  // last-good figures for failed sections while a scope change resets first.
  const committedScope = useRef<string | null>(null);

  // Every figure below comes from an existing admin endpoint; the branch
  // view filter is forwarded the same way every other page forwards it and
  // the server re-resolves + enforces scope on each request.
  const loadAll = useCallback(async () => {
    const myGen = ++generation.current;
    setRefreshing(true);

    const today = manilaToday();
    const monthStart = `${today.slice(0, 7)}-01`;
    const b = branchFilter === 'ALL' ? '' : `&branch=${encodeURIComponent(branchFilter)}`;
    // One counts request feeds both the Transaction Status donut and the four
    // queue panels, so the branch-scoped status totals are fetched once.
    const queuesCounts = api.get<TransactionsListResponse>(`/transactions?limit=1${b}`);

    const requests: Record<SectionKey, () => Promise<unknown>> = {
      today: () => api.get<PaymentsListResponse>(`/payments?date=TODAY&limit=1${b}`),
      collectionsToday: () => api.get<CollectionsResponse>(`/payments/collections?date=TODAY${b}`),
      queues: () => queuesCounts,
      // Newest rows for each active queue. The list endpoint orders oldest ->
      // newest, so the window is read from the end using the server total;
      // ordering by the real activity timestamp happens before render.
      statusQueues: async () => {
        const counts = await queuesCounts;
        const entries = await Promise.all(
          QUEUE_STATUSES.map(async (status) => {
            const total = counts.statusCounts[status] ?? 0;
            if (total === 0) {
              return [status, { total: 0, statusCounts: counts.statusCounts, items: [] }] as const;
            }
            const limit = Math.min(QUEUE_PANEL_LIMIT, total);
            const offset = Math.max(0, total - limit);
            const payload = await api.get<TransactionsListResponse>(
              `/transactions?status=${status}&limit=${limit}&offset=${offset}${b}`,
            );
            return [status, payload] as const;
          }),
        );
        return Object.fromEntries(entries) as StatusQueues;
      },
      inventory: () => api.get<InventoryListResponse>(`/inventory?limit=1${b}`),
      expenses: () =>
        api.get<ExpenseListResponse>(`/expenses?dateFrom=${monthStart}&dateTo=${today}&limit=200${b}`),
      purchases: () =>
        api.get<PurchaseListResponse>(
          `/purchases?status=POSTED&dateFrom=${monthStart}&dateTo=${today}&limit=200${b}`,
        ),
      dashboardFinancials: () =>
        api.get<DashboardFinancials>(
          `/payments/dashboard-financials?period=${encodeURIComponent(period)}${branchFilter === 'ALL' ? '' : `&branch=${encodeURIComponent(branchFilter)}`}`,
        ),
      attention: () => api.get<AttentionResponse>(`/exceptions?limit=5${b}`),
      account: () =>
        api.get<AccountabilityTodayResponse>(`/accountability/summary?date=TODAY&limit=200${b}`),
    };

    const keys = Object.keys(requests) as SectionKey[];
    const results = await Promise.allSettled(keys.map((k) => requests[k]()));
    if (myGen !== generation.current) return; // superseded by a newer load

    const bad: SectionKey[] = [];
    results.forEach((r, i) => {
      if (r.status !== 'fulfilled') bad.push(keys[i]);
    });

    // Same-scope refresh: keep the last good figures for failed sections
    // (stale-data state, flagged by the banner). A branch switch starts from
    // empty instead, so another branch's numbers are never shown under the
    // new filter.
    const scopeChanged = committedScope.current !== branchFilter;
    committedScope.current = branchFilter;
    setData((prev) => {
      const merged: DashboardData = scopeChanged ? { ...EMPTY_DATA } : { ...prev };
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') Object.assign(merged, { [keys[i]]: r.value });
      });
      return merged;
    });
    setFailed(bad);
    setLoading(false);
    setRefreshing(false);
    // A successful refresh with at least one section updates the clock;
    // a total failure keeps the previous timestamp (stale-data signal).
    if (bad.length < keys.length) setUpdatedAt(new Date());
  }, [branchFilter, period]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      await loadAll();
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [loadAll]);

  // Automatic refresh: every 60s while the tab is visible (live queue and
  // figure staleness). Hidden tabs skip the tick — visibility throttling would
  // make it useless anyway — so the next visible tick catches up. Everything
  // else stays on-demand: the Refresh button, and a branch-filter change
  // (user action) which refetches immediately via loadAll's dependency.
  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.hidden) return;
      void loadAll();
    }, 60_000);
    return () => window.clearInterval(id);
  }, [loadAll]);

  const failedLabel = (k: SectionKey): string =>
    ({
      today: 'received-today',
      collectionsToday: 'collections',
      queues: 'queues',
      statusQueues: 'status queues',
      inventory: 'inventory',
      expenses: 'expenses',
      purchases: 'purchases',
      attention: 'attention',
      account: 'accountability',
      dashboardFinancials: 'dashboard-financials',
    })[k];

  /* ---- derived figures (pure, from fetched payloads) ---- */

  const dashboardTrendPoints = useMemo<{ date: string; sales: number; expenses: number }[]>(() => {
    if (!data.dashboardFinancials?.dailySeries) return [];
    return data.dashboardFinancials.dailySeries;
  }, [data.dashboardFinancials]);

  const activeOrders = useMemo(() => {
    const s = data.queues?.statusCounts;
    if (!s) return null;
    const active = (s.PENDING ?? 0) + (s.WASHING ?? 0) + (s.FOLDING ?? 0) + (s.READY ?? 0);
    const parts: string[] = [];
    if (s.PENDING) parts.push(`${s.PENDING} waiting`);
    if (s.WASHING) parts.push(`${s.WASHING} washing`);
    if (s.FOLDING) parts.push(`${s.FOLDING} folding`);
    if (s.READY) parts.push(`${s.READY} ready`);
    return { active, sub: parts.join(' · ') || 'Nothing in the queues right now' };
  }, [data.queues]);

  const monthScope = `${dayLabel(`${manilaToday().slice(0, 7)}-01`)} – ${dayLabel(manilaToday())}`;

  const expensesSummary = useMemo(() => {
    const d = data.expenses;
    if (!d) return null;
    if (d.total > d.items.length) {
      return { exact: false, count: d.total, sumCents: 0 };
    }
    return { exact: true, count: d.total, sumCents: d.items.reduce((s, e) => s + e.amountCents, 0) };
  }, [data.expenses]);

  const purchasesSummary = useMemo(() => {
    const d = data.purchases;
    if (!d) return null;
    if (d.total > d.items.length) {
      return { exact: false, count: d.total, sumCents: 0 };
    }
    return { exact: true, count: d.total, sumCents: d.items.reduce((s, p) => s + p.totalCents, 0) };
  }, [data.purchases]);

  // Real rows per active status, newest activity first. Sorting is applied to
  // the fetched data itself (not just the rendered order), with a stable
  // id tie-breaker for identical timestamps.
  const queuePanels = useMemo(
    () =>
      QUEUE_STATUSES.map((status) => {
        const payload = data.statusQueues?.[status] ?? null;
        const items = payload
          ? [...payload.items].sort(
              (a, b) =>
                queueActivityAt(b, status) - queueActivityAt(a, status) ||
                b.id.localeCompare(a.id),
            )
          : [];
        return {
          status,
          label: PANEL_STATUS_LABELS[status],
          color: STATUS_DONUT_COLORS[PANEL_STATUSES.indexOf(status)] ?? '#94a3b8',
          total: payload?.total ?? 0,
          items,
        };
      }),
    [data.statusQueues],
  );

  const financialTrend = useMemo(() => {
    if (!data.dashboardFinancials?.dailySeries) return null;
    const items = data.dashboardFinancials.dailySeries;
    return items.slice(0, 5);
  }, [data.dashboardFinancials]);

  const reviewCount =
    (data.attention?.severityCounts.ERROR ?? 0) + (data.attention?.severityCounts.ATTENTION ?? 0);

  const processingToday =
    data.account?.items.reduce((sum, row) => sum + (row.counts.processingCompleted ?? 0), 0) ?? 0;
  const discrepanciesToday =
    data.account?.items.reduce((sum, row) => sum + (row.counts.discrepancyEvents ?? 0), 0) ?? 0;

  const netIncome = data.dashboardFinancials
    ? centsToPesos(data.dashboardFinancials.netIncomeCents)
    : '—';
  const totalSales = data.dashboardFinancials
    ? centsToPesos(data.dashboardFinancials.salesCents)
    : '—';
  const totalRevenue = data.dashboardFinancials
    ? centsToPesos(data.dashboardFinancials.revenueCents)
    : '—';

  const firstLoad = loading && !updatedAt;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            Welcome back, {user?.name?.split(' ')[0] ?? 'Admin'}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Viewing: <span className="font-semibold text-slate-700">{scopeLabel}</span>
            {branchFilter !== 'ALL' && ' (view filter — your access covers both branches)'}
            {updatedAt && (
              <span className="ml-2 tabular-nums text-slate-500">Updated {clockTime(updatedAt)}</span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="flex items-center gap-2 rounded-xl bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700">
            <Icon name="shield" className="h-4 w-4" />
            Admin · Cross-branch
          </span>
          <div className="flex items-center gap-2">
            <label htmlFor="period-select" className="text-xs font-medium text-slate-500">Period:</label>
            <select
              id="period-select"
              value={period}
              onChange={(e) => setPeriod(e.target.value as FinancialPeriod)}
              className="h-10 rounded-xl border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent"
              aria-label="Financial period filter"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>
          <button
            type="button"
            onClick={() => void loadAll()}
            disabled={refreshing}
            className="flex h-10 items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 transition-colors hover:border-slate-400 hover:text-slate-900 disabled:opacity-60"
          >
            {refreshing ? <Spinner className="h-4 w-4" /> : <Icon name="refresh" className="h-4 w-4" />}
            Refresh
          </button>
        </div>
      </header>

      {failed.length > 0 && (
        <ErrorBanner
          message={`Some dashboard data could not be loaded (${failed
            .map(failedLabel)
            .join(', ')}). The figures shown are from the last successful load. Use Refresh to retry.`}
        />
      )}

      {/* ---- Dashboard layout (matches Sample Layout/Panel Dashboard Layout.png) ----
        One 12-column grid:
        Left (8 cols): three equal KPI cards in one row, then Sales Performance.
        Right (4 cols): Transaction Status (top-right, shorter than Sales) then
          This month directly underneath.
        The four status queues span all 12 columns beneath both columns. The
          row above stretches both columns to the same height; within the right
          column This month grows into the remaining space, so its bottom edge
          aligns with the bottom of Sales Performance.
      */}
      <div className="grid grid-cols-12 gap-4">
        {/* Left column: KPI + Sales Performance */}
        <div className="col-span-8 flex flex-col gap-4">
          {/* KPI cards */}
          <div className="grid grid-cols-3 gap-4">
            {firstLoad ? (
              <>
                <Skeleton className="h-[106px]" />
                <Skeleton className="h-[106px]" />
                <Skeleton className="h-[106px]" />
              </>
            ) : (
              <>
                <KpiCard
                  delay="0s"
                  label="Net income"
                  value={netIncome}
                  sub="Sales minus expenses and purchases · Asia/Manila date"
                  icon="chart"
                />
                <KpiCard
                  delay="0.05s"
                  label="Total sales"
                  value={totalSales}
                  sub="Billed transaction volume · Asia/Manila date"
                  icon="dollar"
                />
                <KpiCard
                  delay="0.1s"
                  label="Total revenue"
                  value={totalRevenue}
                  sub="Collected payments · Asia/Manila date"
                  icon="scan"
                />
              </>
            )}
          </div>

          {/* Sales Performance */}
          <Card className={`animate-rise flex-1 p-5 ${PANEL_HOVER}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Sales performance</h2>
                <div className="mt-2 flex flex-wrap gap-4 text-xs text-slate-600">
                  <span className="flex items-center gap-1.5">
                    <span className="h-3 w-3 rounded bg-accent" />
                    Total sales
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="h-3 w-3 rounded bg-amber-500" />
                    Expenses
                  </span>
                </div>
              </div>
              <span className="flex items-center gap-2 rounded-lg bg-accent-soft px-2.5 py-1 text-xs font-semibold text-accent-deep">
                <span className="h-2 w-2 rounded-full bg-accent" aria-hidden="true" />
                {period.charAt(0).toUpperCase() + period.slice(1)} overview
              </span>
            </div>
            <div className="mt-4">
              {firstLoad ? (
                <Skeleton className="h-64" />
              ) : data.dashboardFinancials ? (
                <TrendChart
                  points={dashboardTrendPoints}
                  emptyText="No financial data available for the selected period."
                />
              ) : (
                <p className="py-16 text-center text-sm text-slate-500">
                  Trend unavailable — use Refresh to retry.
                </p>
              )}
            </div>
          </Card>
        </div>

        {/* Right column: Transaction Status + This month. Stretches to the
            row height (Sales Performance); This month takes the remainder. */}
        <div className="col-span-4 flex flex-col gap-4">
          {/* Transaction Status */}
          <Card className={`animate-rise p-4 ${PANEL_HOVER}`} style={{ animationDelay: '0.08s' }}>
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-slate-900">Transaction Status</h2>
                <p className="mt-0.5 text-xs text-slate-600">
                  {branchFilter === 'ALL' ? 'All branches' : scopeLabel}
                </p>
              </div>
              <span className="text-xs text-slate-600">
                {(() => {
                  const s = data.queues?.statusCounts ?? {};
                  const total = PANEL_STATUSES.reduce((sum, st) => sum + (s[st] ?? 0), 0);
                  return `${total} transaction${total !== 1 ? 's' : ''}`;
                })()}
              </span>
            </div>
            <div className="mt-3">
              {firstLoad ? (
                <div className="flex justify-center"><Skeleton className="h-40 w-40 rounded-full" /></div>
              ) : data.queues ? (
                (() => {
                  const statusCounts = data.queues.statusCounts ?? {};
                  return (
                    <div className="flex items-center gap-4">
                      <div className="relative h-40 w-40 shrink-0">
                        <StatusDonutCompact statusCounts={statusCounts} />
                      </div>
                      <ul className="flex-1 space-y-1.5 text-xs">
                        {PANEL_STATUSES.map((status) => {
                          const count = statusCounts[status] ?? 0;
                          if (count === 0) return null;
                          return (
                            <li key={status} className="flex items-center justify-between gap-2">
                              <span className="flex items-center gap-1.5 text-slate-600">
                                <span
                                  className="h-2 w-2 rounded-full"
                                  style={{ background: STATUS_DONUT_COLORS[PANEL_STATUSES.indexOf(status)] }}
                                  aria-hidden="true"
                                />
                                {PANEL_STATUS_LABELS[status]}
                              </span>
                              <span className="font-semibold tabular-nums text-slate-900">{count}</span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                })()
              ) : (
                <p className="py-6 text-center text-xs text-slate-500">Status unavailable — use Refresh to retry.</p>
              )}
            </div>
          </Card>

          {/* This month */}
          <Card className={`animate-rise flex flex-1 flex-col p-4 ${PANEL_HOVER}`} style={{ animationDelay: '0.12s' }}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold text-slate-900">This month</h2>
                <p className="mt-0.5 text-xs text-slate-600">Month-to-date · {monthScope}</p>
              </div>
              <Icon name="reports" className="h-5 w-5 text-slate-500" />
            </div>
            <div className="mt-3 grid flex-1 grid-cols-2 gap-3">
              <div className={`rounded-xl border border-slate-200 bg-slate-50 p-3.5 ${PANEL_HOVER}`}>
                <p className="text-[13px] font-medium text-slate-600">Operating expenses</p>
                <p className="mt-1 text-lg font-bold tabular-nums text-slate-900">
                  {expensesSummary
                    ? expensesSummary.exact
                      ? centsToPesos(expensesSummary.sumCents)
                      : '≥ ' + centsToPesos(expensesSummary.sumCents)
                    : '—'}
                </p>
                <p className="mt-0.5 text-xs leading-4 text-slate-600">
                  {expensesSummary
                    ? `${expensesSummary.count} expense${expensesSummary.count === 1 ? '' : 's'} recorded${
                        expensesSummary.exact ? '' : ' (sum covers the first 200)'
                      }`
                    : 'Unavailable'}
                </p>
                <Link
                  to="/admin/expenses"
                  className="mt-3 inline-block text-xs font-semibold text-accent-deep hover:underline"
                >
                  Open expenses →
                </Link>
              </div>
              <div className={`rounded-xl border border-slate-200 bg-slate-50 p-3.5 ${PANEL_HOVER}`}>
                <p className="text-[13px] font-medium text-slate-600">Posted purchases</p>
                <p className="mt-1 text-lg font-bold tabular-nums text-slate-900">
                  {purchasesSummary
                    ? purchasesSummary.exact
                      ? centsToPesos(purchasesSummary.sumCents)
                      : '≥ ' + centsToPesos(purchasesSummary.sumCents)
                    : '—'}
                </p>
                <p className="mt-0.5 text-xs leading-4 text-slate-600">
                  {purchasesSummary
                    ? `${purchasesSummary.count} posted purchase${purchasesSummary.count === 1 ? '' : 's'}${
                        purchasesSummary.exact ? '' : ' (sum covers the first 200)'
                      }`
                    : 'Unavailable'}
                </p>
                <Link
                  to="/admin/purchasing"
                  className="mt-3 inline-block text-xs font-semibold text-accent-deep hover:underline"
                >
                  Open purchasing →
                </Link>
              </div>
            </div>
          </Card>

        </div>

        {/* ---- Status queues: Pending / Washing / Folding / Ready ---- */}
        <div className="col-span-12 grid grid-cols-4 gap-4">
          {queuePanels.map((panel, index) => (
            <Card
              key={panel.status}
              className={`animate-rise flex flex-col p-4 ${PANEL_HOVER}`}
              style={{ animationDelay: `${0.16 + index * 0.02}s` }}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ background: panel.color }}
                    aria-hidden="true"
                  />
                  <h2 className="truncate text-sm font-semibold text-slate-900">{panel.label}</h2>
                </div>
                <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold tabular-nums text-slate-700">
                  {data.statusQueues ? panel.total : '—'}
                </span>
              </div>
              <p className="mt-0.5 text-[11px] text-slate-600">Newest first</p>

              <div className="mt-3 min-h-0 flex-1">
                {firstLoad ? (
                  <div className="space-y-2">
                    <Skeleton className="h-14" />
                    <Skeleton className="h-14" />
                    <Skeleton className="h-14" />
                  </div>
                ) : !data.statusQueues ? (
                  <p className="py-8 text-center text-xs text-slate-600">
                    Unavailable — use Refresh to retry.
                  </p>
                ) : panel.items.length === 0 ? (
                  <p className="py-8 text-center text-xs text-slate-600">
                    No {panel.label.toLowerCase()} transactions.
                  </p>
                ) : (
                  <ul className="queue-scroll max-h-[17rem] divide-y divide-slate-100 overflow-y-auto pr-1">
                    {panel.items.map((tx) => {
                      const minutes = Math.max(
                        0,
                        Math.round((Date.now() - queueActivityAt(tx, panel.status)) / 60000),
                      );
                      return (
                        <li key={tx.id} className="py-2.5">
                          <div className="flex items-start justify-between gap-2">
                            <p className="min-w-0 truncate text-sm font-semibold text-slate-900">
                              {tx.number}
                            </p>
                            <span className="shrink-0 text-[11px] tabular-nums text-slate-600">
                              {formatAge(minutes)}
                            </span>
                          </div>
                          <p className="mt-0.5 truncate text-xs text-slate-600">{tx.customerName}</p>
                          <div className="mt-1 flex items-center justify-between gap-2 text-[11px] text-slate-600">
                            <span className="min-w-0 truncate">
                              {tx.branch.code} · {PANEL_STATUS_LABELS[panel.status]} ·{' '}
                              {tx.paymentMethod ?? 'No payment method'}
                            </span>
                            <span className="shrink-0 font-semibold tabular-nums text-slate-800">
                              {centsToPesos(tx.totalCents)}
                            </span>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {data.statusQueues && panel.total > panel.items.length && (
                <p className="mt-2 text-[10px] leading-4 text-slate-600">
                  Newest {panel.items.length} of {panel.total}
                </p>
              )}
            </Card>
          ))}
        </div>
      </div>

      {/* ---- Needs Attention (existing data flow) ---- */}
      {data.attention && (
        <Card className={`animate-rise p-5 ${PANEL_HOVER}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <span
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${
                  reviewCount > 0 ? 'bg-rose-50 text-rose-600' : 'bg-emerald-50 text-emerald-600'
                }`}
              >
                <Icon name={reviewCount > 0 ? 'alert' : 'check'} className="h-5 w-5" />
              </span>
              <div>
                <p className="text-sm font-semibold text-slate-800">Attention Required</p>
                <p className="mt-0.5 text-sm text-slate-500">
                  {reviewCount > 0 ? (
                    <>
                      {data.attention.severityCounts.ERROR} error
                      {data.attention.severityCounts.ERROR === 1 ? '' : 's'},{' '}
                      {data.attention.severityCounts.ATTENTION} discrepancy{' '}
                      {data.attention.severityCounts.ATTENTION === 1 ? 'finding' : 'findings'} ·{' '}
                      {data.attention.total} observation{data.attention.total === 1 ? '' : 's'} total
                    </>
                  ) : (
                    <>
                      No errors or count discrepancies · {data.attention.total} observation
                      {data.attention.total === 1 ? '' : 's'} (aging is informational)
                    </>
                  )}
                </p>
              </div>
            </div>
            <Link
              to="/admin/exceptions"
              className="rounded-xl bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-200"
            >
              Open Needs Attention
            </Link>
          </div>
          {data.attention.items.length > 0 && (
            <ul className="mt-3 space-y-1.5 border-t border-slate-200 pt-3 text-sm">
              {data.attention.items.slice(0, 4).map((item) => (
                <li key={item.id} className="flex items-center justify-between gap-2">
                  <span className="flex min-w-0 items-center gap-2">
                    <Badge tone={SEVERITY_TONE[item.severity]}>{item.severity}</Badge>
                    <span className="truncate text-slate-700">
                      {item.categoryLabel} · {item.transaction.number}
                    </span>
                  </span>
                  <span className="shrink-0 text-xs font-medium text-slate-500">
                    {formatAge(item.ageMinutes)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {/* ---- Accountability today (existing data flow) ---- */}
      {data.account && (
        <Card className={`animate-rise flex flex-wrap items-center justify-between gap-4 p-5 ${PANEL_HOVER}`}>
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
              <Icon name="users" className="h-5 w-5" />
            </span>
            <div>
              <p className="text-sm font-semibold text-slate-800">Accountability today</p>
              <p className="mt-0.5 text-sm text-slate-500">
                {data.account.total} employee{data.account.total === 1 ? '' : 's'} with recorded activity ·{' '}
                {processingToday} processing action{processingToday === 1 ? '' : 's'} ·{' '}
                {discrepanciesToday} recorded item{' '}
                {discrepanciesToday === 1 ? 'discrepancy' : 'discrepancies'} (
                {data.account.timeZone} date)
              </p>
            </div>
          </div>
          <Link
            to="/admin/accountability"
            className="rounded-xl bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-200"
          >
            Open Employee Accountability
          </Link>
        </Card>
      )}

      {/* ---- Quick access (existing navigation tiles, preserved) ---- */}
      <section>
        <h2 className="mb-3 text-lg font-semibold text-slate-900">Quick access</h2>
        <div className="grid grid-cols-4 gap-4">
          {flattenNav(ADMIN_NAV)
            .filter((item) => item.id !== 'dashboard')
            .map((item, i) => (
              <Link
                key={item.id}
                to={item.path}
                style={{ animationDelay: `${Math.min(i, 11) * 0.04}s` }}
                className="animate-rise group rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_1px_3px_0_rgba(0,0,0,0.08),0_1px_2px_-1px_rgba(0,0,0,0.04)] transition-all hover:-translate-y-0.5 hover:border-accent/40 hover:shadow-[0_0_0_1px_rgba(36,152,232,0.25),0_22px_44px_-26px_rgba(36,152,232,0.35)]"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-slate-600 transition-colors group-hover:bg-accent-soft group-hover:text-accent">
                  <Icon name={MODULE_ICONS[item.id] ?? 'dashboard'} className="h-6 w-6" />
                </span>
                <h2 className="mt-3 font-semibold text-slate-900">{item.label}</h2>
                <p className="mt-0.5 text-sm text-slate-500">{MODULE_HINTS[item.id] ?? ''}</p>
              </Link>
            ))}
        </div>
      </section>
    </div>
  );
}

/* ============================================================
 * KPI stat card (RetailPro-style: label, accent icon, big value)
 * ============================================================ */

function KpiCard({
  label,
  value,
  sub,
  icon,
  delay,
  tone = 'accent',
}: {
  label: string;
  value: string;
  sub: string;
  icon: IconName;
  delay: string;
  tone?: 'accent' | 'warn';
}) {
  return (
    <div
      className={`animate-rise flex h-full flex-col justify-between rounded-2xl border border-slate-200 bg-white p-3.5 shadow-[0_1px_3px_0_rgba(0,0,0,0.08),0_1px_2px_-1px_rgba(0,0,0,0.04)] hover:-translate-y-0.5 ${PANEL_HOVER}`}
      style={{ animationDelay: delay }}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-xs font-medium text-slate-600">{label}</span>
        <Icon
          name={icon}
          className={`h-5 w-5 shrink-0 ${tone === 'warn' ? 'text-amber-600' : 'text-accent'}`}
        />
      </div>
      <p className="mt-1 text-[22px] font-semibold leading-7 tabular-nums tracking-tight text-slate-900">{value}</p>
      <p className="mt-1 text-[11px] leading-[15px] text-slate-600">{sub}</p>
    </div>
  );
}
